import { useEffect, useRef, useState } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import { DynamicUrlTileLayer } from "./DynamicUrlTileLayer";
import { getTileBounds } from "util/tiles/getTileBounds";
import { ILayerBounds } from "types/IForecastMap";
import { getTilePathByBounds } from "util/tiles/getTilePathByBounds";
import { removeMapLayersById } from "../util/removeMapLayersById";

interface ForecastTileLayerProps {
  visibility: boolean;
  layerSource: {
    bounds: ILayerBounds;
    path: { [key: string]: string };
  } | null;
  minZoom?: number;
  maxZoom?: number;
  currentZoom?: number;
  opacity?: number;
  zIndex?: number;
}

const ForecastTileLayer: React.FC<ForecastTileLayerProps> = ({
  visibility = false,
  layerSource = null,
  minZoom = 3,
  maxZoom = 10,
  opacity = 0.7,
  zIndex = 650,
  currentZoom,
}) => {
  const map = useMap();
  const [currentLayer, setCurrentLayer] = useState<L.TileLayer | null>(null);
  const isLoadingRef = useRef(false);

  useEffect(() => {
    if (!visibility) {
      removeMapLayersById(map, ["forecastTileLayer"]);
      setCurrentLayer(null);
      isLoadingRef.current = false;
    }
  }, [visibility]);

  const trackTileLoading = (layer: L.TileLayer, onComplete: () => void) => {
    let loadingTiles = 0;
    let loadedTiles = 0;

    const startLoading = () => {
      loadingTiles++;
    };

    const finishLoading = () => {
      setTimeout(() => {
        loadedTiles++;
        if (loadedTiles >= loadingTiles) {
          onComplete();
        }
      }, 250);
    };

    layer.on("loading", startLoading);
    layer.on("load", finishLoading);
  };

  useEffect(() => {
    if (!map || !visibility || !layerSource) return;

    if (isLoadingRef.current) {
      return;
    }
    isLoadingRef.current = true;

    const newLayer = new DynamicUrlTileLayer("", {
      id: "forecastTileLayer",
      minZoom,
      maxZoom,
      opacity: 0.01,
      zIndex,
      getDynamicUrl: (coords) => {
        const tileBounds = getTileBounds(coords.x, coords.y, coords.z);
        return getTilePathByBounds(
          layerSource.path,
          layerSource.bounds,
          tileBounds,
          coords
        );
      },
    });

    if (currentZoom && currentZoom > maxZoom) {
      if (currentLayer) {
        map.removeLayer(currentLayer);
      }
    }

    trackTileLoading(newLayer, () => {
      newLayer.setOpacity(opacity);
      if (currentLayer) {
        map.removeLayer(currentLayer);
      }
      isLoadingRef.current = false;
    });

    newLayer.addTo(map);

    setTimeout(() => {
      const container = newLayer.getContainer?.();
      if (container) {
        container.style.pointerEvents = "none";
      }
    }, 0);
    setCurrentLayer(newLayer);
  }, [layerSource]);

  return null;
};

export default ForecastTileLayer;
